/**
 * useCompanySettings — ログインユーザーの自社設定（購入エンタイトルメント／レポート開示）を取得する。
 *
 * バックエンド `GET /company/settings`（会社所属の全ロールが自社分のみ取得）から取得し、
 * サイドバーのレポートメニュー表示制御などに使う。モバイルの useCompanySettingsStore と同等。
 *
 * フォールバック方針: 取得失敗時は全機能ON（現状維持）。設定取得の不調でメニューが消えないようにする。
 * 反映: ウィンドウフォーカス時にも再取得し、管理画面での開示ON/OFF変更をリロードなしで反映する。
 */
import { useEffect, useState } from 'react';
import { createAuthenticatedClient } from '../api/axiosClient';

export interface CompanySettings {
  /** GB人財の利用可否 */
  hasJinzai: boolean;
  /** GB組織の利用可否 */
  hasSoshiki: boolean;
  /** GBなびの利用可否 */
  hasNavi: boolean;
  /** レポートの表示可否（社長が見るレポート全般の開示） */
  reportVisible: boolean;
  /** 社員向け「個人レポート」ボタンの表示可否 */
  employeeReportButtonVisible: boolean;
}

/** 既定値（全機能ON＝現状維持）。取得前/取得失敗時のフォールバック。 */
export const DEFAULT_COMPANY_SETTINGS: CompanySettings = {
  hasJinzai: true,
  hasSoshiki: true,
  hasNavi: true,
  reportVisible: true,
  employeeReportButtonVisible: true,
};

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;
const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

/**
 * 自社設定を取得する。`enabled` が false の間は取得せず既定値を返す（未認証時など）。
 */
export function useCompanySettings(enabled: boolean = true): CompanySettings {
  const [settings, setSettings] = useState<CompanySettings>(DEFAULT_COMPANY_SETTINGS);

  useEffect(() => {
    if (!enabled) return;
    let active = true;

    const load = () => {
      getApiClient()
        .get<Partial<CompanySettings>>('/company/settings')
        .then((res) => {
          if (active) setSettings({ ...DEFAULT_COMPANY_SETTINGS, ...(res.data ?? {}) });
        })
        .catch(() => {
          // 取得失敗は全機能ON（現状維持）にフォールバック。メニューを消さない。
          if (active) setSettings(DEFAULT_COMPANY_SETTINGS);
        });
    };

    load();
    // 管理画面での開示変更を、ページを離れて戻る/タブ復帰時に反映（リロード不要）。
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      active = false;
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled]);

  return settings;
}
